import Tooltip from '@material-ui/core/Tooltip';
import { MNEMONICS } from 'constants/mnemonics';
import { getActiveTriptankMnemonicFromLiveData } from 'pages/DataManagementDataQuality/mnemonicsHelper';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { DepthMnemonicMappingDTO } from 'types/mnemonicsMapping';

import styles from './MnemonicMappingIndicator.module.scss';

export interface Props {
  mnemonic: string;
  isKDMapping?: boolean;
  isFlowInBstNA: boolean;
  isFlowOutAVNA?: boolean;
  isFlowOutPCNA?: boolean;
  isLive?: boolean;
  isDatasetWellNotASensorWell?: boolean;
  scopeMnemonics: string[];
  mnemonicsDepthData?: DepthMnemonicMappingDTO;
  liveMnemonics?: Record<string, string>;
}

const MnemonicMappingIndicator: FC<Props> = ({
  mnemonic,
  isKDMapping,
  isFlowOutAVNA,
  isFlowOutPCNA,
  isFlowInBstNA,
  scopeMnemonics,
  isLive,
  isDatasetWellNotASensorWell,
  liveMnemonics,
  mnemonicsDepthData,
}) => {
  const oneHourAgo = new Date();
  oneHourAgo.setHours(oneHourAgo.getHours() - 1);
  const intl = useIntl();
  const indicator = {
    green: {
      styles: styles.greenTag,
      description: intl.formatMessage({
        id: isDatasetWellNotASensorWell
          ? `dataManagement.dataQuality.table.mnemonicBoardTooltip.notSensorWellDatasetGreen`
          : `dataManagement.dataQuality.table.mnemonicBoardTooltip.green`,
      }),
    },
    grey: {
      styles: styles.greyTag,
      description: intl.formatMessage({
        id: `dataManagement.dataQuality.table.mnemonicBoardTooltip.grey`,
      }),
    },
    red: {
      styles: styles.redTag,
      description: intl.formatMessage({
        id: isDatasetWellNotASensorWell
          ? `dataManagement.dataQuality.table.mnemonicBoardTooltip.notSensorWellDatasetRed`
          : `dataManagement.dataQuality.table.mnemonicBoardTooltip.red`,
      }),
    },
    yellow: {
      styles: styles.yellowTag,
      description: intl.formatMessage({
        id: `dataManagement.dataQuality.table.mnemonicBoardTooltip.yellow`,
      }),
    },
  };

  const getMappingIndicator = () => {
    if (!liveMnemonics || !scopeMnemonics.includes(mnemonic)) return indicator.grey;

    if (mnemonic === MNEMONICS.FLOWIN_BST && isFlowInBstNA) {
      return indicator.grey;
    }

    if (mnemonic === MNEMONICS.INCC) {
      // yellow display for training datasets where the quality of depth mnemonics data cannot be assessed
      if (mnemonicsDepthData === undefined) {
        return indicator.yellow;
      }
      if (!mnemonicsDepthData[mnemonic]?.live) return indicator.red;

      return indicator.green;
    }

    if (!liveMnemonics[mnemonic] || (isLive && new Date(liveMnemonics[mnemonic]) <= oneHourAgo))
      return indicator.red;

    return indicator.green;
  };

  const getKDMappingIndicator = () => {
    if (mnemonic === MNEMONICS.TTVOL01 && liveMnemonics) {
      const tripTankMnemonic = getActiveTriptankMnemonicFromLiveData(liveMnemonics);
      if (
        !tripTankMnemonic ||
        !liveMnemonics[tripTankMnemonic] ||
        (isLive && new Date(liveMnemonics[mnemonic]) < oneHourAgo)
      ) {
        return indicator.red;
      }

      return indicator.green;
    }
    if (mnemonic === MNEMONICS.FLOWOUTAV && isFlowOutAVNA) {
      return indicator.grey;
    }
    if (mnemonic === MNEMONICS.FLOWOUTPC && isFlowOutPCNA) {
      return indicator.grey;
    }

    return getMappingIndicator();
  };

  const mnemonicIndicator: {
    styles: string;
    description: string;
  } = isKDMapping ? getKDMappingIndicator() : getMappingIndicator();

  return (
    <Tooltip arrow title={mnemonicIndicator.description}>
      <div className={mnemonicIndicator.styles} />
    </Tooltip>
  );
};

export default MnemonicMappingIndicator;
